/// <amd-module name="VSS/Platform/Location" />
import { IVssPageContext, IVssService, Services, TeamFoundationHostType, VssService } from "./Context";
import { IVssContributionService } from "./Contribution";
import { IVssPageService } from "./Page";
import { combineUrlPaths, IParsedRoute, parseRouteTemplate, routeUrl as Util_routeUrl } from "./Util/Url";

/**
 * Service for obtaining URLs to VSS resources.
 */
export interface IVssLocationService extends IVssService {
    /**
     * Gets the location of an asset that was included in the request. To ensure an asset
     * location is available add the asset to _sharedData for a contribution that is within
     * the current request.
     *
     * @param asset The name of the asset. This is in the form <providerId/assetType>.
     *
     *  This will get the location for the asset images/image01.png from the ms.vss-web provider.
     *  Example: "ms.vss-web/images/image01.png"
     */
    getAssetLocation(asset: string): string;

    /**
     * Gets the service instance type id that the given REST resource area points to
     *
     * @param resourceAreaId Id of the resource area
     */
    getServiceInstanceTypeForResourceArea(resourceAreaId: string): string | undefined;

    /**
     * Gets the location of a remote service at a given host type, considering locations
     * that were populated using IClientLocationProviderService (within a data provider or request handler).
     *
     * @param serviceInstanceType The GUID of the service instance type to lookup
     * @param hostType The host type to lookup (defaults to the host type of the current page data)
     */
    getServiceLocation(serviceInstanceType?: string, hostType?: TeamFoundationHostType): string;

    /**
     * Gets the location of a remote service at a given host type, potentially issuing a remote network
     * call to get the location if it was not included in page data.
     *
     * @param serviceInstanceType The GUID of the service instance type to lookup
     * @param hostType The host type to lookup (defaults to the host type of the current page data)
     */
    getServiceLocationAsync(serviceInstanceType?: string, hostType?: TeamFoundationHostType): Promise<string>;

    /**
     * Attemps to create a url for the specified route template and paramaters.  The url will include host path.
     * For example, if the page url is https://dev.azure.com/foo and you try to create admin settings url for project "bar",
     * the output will be /foo/bar/_admin.
     *
     * @param routeId Id of the route contribution
     * @param routeValues Route value replacements
     * @param hostPath Optional host path to use rather than the default host path for the page.
     */
    routeUrl(routeId: string, routeValues?: { [key: string]: string }, hostPath?: string): string;

    /**
     * Attemps to create an absolute url for the specified route template and paramaters.  The url will include host path.
     * For example, if the page url is https://dev.azure.com/foo and you try to create admin settings url for project "bar",
     * the output will be https://dev.azure.com/foo/bar/_admin.
     *
     * @param routeId Id of the route contribution
     * @param routeValues Route value replacements
     * @param hostPath Optional host path to use rather than the default host path for the page.
     */
     routeAbsoluteUrl(routeId: string, routeValues?: { [key: string]: string }, hostPath?: string): string;
}

interface ILocationsContributionData {
    [serviceInstanceType: string]: { [hostType: number]: string };
}

interface IRoutesContributionData {
    [routeId: string]: string[];
}

interface IResourceAreasContributionData {
    [resourceAreaId: string]: string;
}

class VssLocationService extends VssService implements IVssLocationService {
    private _parsedRoutes: { [key: string]: IParsedRoute[] } = {};

    public getAssetLocation(asset: string): string {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const assets = contributionService.getSharedData<{ [asset: string]: string }>("_assets");
        if (assets) {
            return assets[asset];
        }

        throw new Error(`Could not find asset for service ${asset}. Ensure that the asset is added through a shared data value.`);
    }

    public getServiceInstanceTypeForResourceArea(resourceAreaId: string): string | undefined {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const resourceAreas = contributionService.getSharedData<IResourceAreasContributionData>("_resourceAreas");
        if (resourceAreas) {
            return resourceAreas[resourceAreaId];
        }
        return undefined;
    }

    public getServiceLocation(serviceInstanceType?: string, hostType?: TeamFoundationHostType): string {
        const location = this.getServiceLocationLocal(serviceInstanceType, hostType);
        if (!location) {
            throw new Error(
                `Could not find location for service ${serviceInstanceType} and hostType ${hostType}. Ensure that the requested service location is included in shared data.`
            );
        }
        return location;
    }

    public getServiceLocationAsync(serviceInstanceType?: string, hostType?: TeamFoundationHostType): Promise<string> {
        const location = this.getServiceLocationLocal(serviceInstanceType, hostType);
        if (location) {
            return Promise.resolve(location);
        }

        const pageData = this.pageContext.getService<IVssPageService>("IVssPageService").getData()!;
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");

        const requestParams = {
            _sharedData: {
                locations: [
                    {
                        ServiceInstanceId: !serviceInstanceType || !pageData.isHosted ? pageData.serviceInstanceType : serviceInstanceType,
                        HostType: hostType || pageData.hostType
                    }
                ]
            }
        };

        return contributionService.getDataAsync("ms.vss-web.shared-data", requestParams).then(() => {
            return this.getServiceLocation(serviceInstanceType, hostType);
        });
    }

    public routeAbsoluteUrl(routeId: string, routeValues?: { [key: string]: string }, hostPath?: string): string { 
        const relativeUrl = this.routeUrl(routeId, routeValues, hostPath);
        
        //IE10 and older versions do not support window.location.origin
        const baseUrl = window.location.origin || (window.location.protocol + "//" + window.location.hostname + (window.location.port ? ':' + window.location.port : ''));

        return baseUrl + relativeUrl;
    }

    public routeUrl(routeId: string, routeValues?: { [key: string]: string }, hostPath?: string): string {
        // Check to see if we have parsed routes for this route id
        let routeCollection: IParsedRoute[] = this._parsedRoutes[routeId];

        // If we have not yet parsed routes for this ID, attempt to parsed them now.
        if (!routeCollection) {
            // Look for the route templates
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            const routeData = contributionService.getSharedData<IRoutesContributionData>("_routes");

            // If we found route templates, then look for ones matching the specified id.
            if (routeData) {
                const routes = routeData[routeId];
                if (routes) {
                    routeCollection = [];

                    // Parse the available templates
                    routes.forEach(r => {
                        routeCollection.push(parseRouteTemplate(r));
                    });

                    this._parsedRoutes[routeId] = routeCollection;
                }
            }
        }

        // If we were able to find a set of route templates for the route id, then attempt to create a url.
        if (routeCollection) {
            // Get the relative url for this route collection and values
            const relativeUrl = Util_routeUrl(routeCollection, routeValues || {});

            // Add the host path
            if (!hostPath) {
                const pageData = this.pageContext.getService<IVssPageService>("IVssPageService").getData();
                if (pageData && pageData.hostPath) {
                    hostPath = pageData.hostPath;
                }
            }

            if (hostPath) {
                const hostUrl = encodeURI(hostPath);
                return combineUrlPaths(hostUrl, relativeUrl);
            }
        }

        throw new Error(`Could not find route for route id ${routeId}. Ensure that the requested route is added to routes shared data.`);
    }

    private getServiceLocationLocal(serviceInstanceType?: string, hostType?: TeamFoundationHostType): string | undefined {
        const pageData = this.pageContext.getService<IVssPageService>("IVssPageService").getData()!;

        if (!hostType) {
            hostType = pageData.hostType;
        }

        if (!serviceInstanceType || !pageData.isHosted) {
            // get the service instance type of the host. For on-prem, always use the on-prem
            // service instance type id.
            serviceInstanceType = pageData.serviceInstanceType;
        }

        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const locations = contributionService.getSharedData<ILocationsContributionData>("_locations");
        if (locations) {
            const serviceLocations = locations[serviceInstanceType];
            if (serviceLocations) {
                const location = serviceLocations[hostType];
                if (location) {
                    return location;
                }
            }
        }

        return undefined;
    }
}

Services.add("IVssLocationService", { serviceFactory: VssLocationService });

/**
 * Gets the location of an asset that was included in the request. To ensure an asset
 * location is available add the asset to _sharedData for a contribution that is within
 * the current request.
 *
 * @param pageContext The active pageContext.
 * @param asset The name of the asset. This is in the form <providerId/assetType>.
 *
 *  This will get the location for the asset images/image01.png from the ms.vss-web provider.
 *  Example: "ms.vss-web/images/image01.png"
 */
export function getAssetLocation(pageContext: IVssPageContext, asset: string): string {
    const locationService = pageContext.getService<IVssLocationService>("IVssLocationService");
    return locationService.getAssetLocation(asset);
}

/**
 * Gets the location of a remote service at a given host type, considering locations
 * that were populated using IClientLocationProviderService (within a data provider or request handler).
 *
 * @param pageContext The active pageContext.
 * @param serviceInstanceType The GUID of the service instance type to lookup
 * @param hostType The host type to lookup (defaults to the host type of the current page data)
 */
export function getServiceLocation(pageContext: IVssPageContext, serviceInstanceType: string | undefined, hostType?: TeamFoundationHostType): string {
    const locationService = pageContext.getService<IVssLocationService>("IVssLocationService");
    return locationService.getServiceLocation(serviceInstanceType, hostType);
}

/**
 * Attemps to create a url for the specified route template and paramaters.  The url will include host path.
 * For example, if the page url is https://foo.visualstudio.com and you try to create admin settings url for project "bar",
 * the output will be /foo/_admin.
 *
 * @param pageContext The active pageContext.
 * @param serviceInstanceType The GUID of the service instance type to lookup
 * @param hostType The host type to lookup (defaults to the host type of the current page data)
 * @param hostPath Host path to use when forming url.
 */
export function routeUrl(pageContext: IVssPageContext, routeId: string, routeValues?: { [key: string]: string }, hostPath?: string): string {
    const locationService = pageContext.getService<IVssLocationService>("IVssLocationService");
    return locationService.routeUrl(routeId, routeValues, hostPath);
}
